import React, { useEffect, useState } from "react"
import { Recommendation } from "pages/Application/Recommendation/Recommendation"
import { ApplicationDataProps } from "pages/Application/Recommendation/types"
import styled from "styled-components"
import axios, { AxiosResponse } from "axios"
import { useParams } from "react-router-dom"
import { ErrorDisplay } from "components/molecules"
import { Loader, LoaderTheme } from "components/atoms/Loader"
import devices from "styles/devices"
import { MY_MOJO_API } from "../../Config"
import { useApplicationSummaryEnhanced } from "hooks/useApplicationSummaryEnhanced"
import * as Sentry from "@sentry/browser"

const Root = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  margin: "64px 16px 0 16px",
  maxWidth: 848,

  [`@media screen and ${devices.bp_xl}`]: {
    minWidth: 768,
    margin: "8.5em auto 0",
  },
})

const LoaderContainer = styled.div({
  display: "flex",
  justifyContent: "center",

  [`@media screen and ${devices.bp_xl}`]: {
    width: "34em",
  },
})

const ErrorContainer = styled.div({
  marginTop: 100,
})

const RenderError = () => (
  <ErrorContainer>
    <ErrorDisplay>Could not find preview token</ErrorDisplay>
  </ErrorContainer>
)

const RenderLoading = () => (
  <LoaderContainer>
    <Loader size={150} theme={LoaderTheme.Outline} />
  </LoaderContainer>
)

export const RecommendationPreview = () => {
  const { token, timestamp } = useParams<{ token: string; timestamp: string }>()
  const [isLoading, setIsLoading] = useState(true)
  const [applicationData, setApplicationData] = useState<ApplicationDataProps | null>(null)
  const { application, hasLoaded } = useApplicationSummaryEnhanced({ applicationData })
  const [isError, setIsError] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  useEffect(() => {
    getApplicationData(token, timestamp)
      .then((data) => {
        setApplicationData(data)
      })
      .catch((error) => {
        setIsError(true)
        Sentry.captureException(
          "An error occurred while loading application data in RecommendationPreview",
          {
            extra: {
              ...(error?.message && { error: error.message }),
              token,
              timestamp,
            },
          }
        )
      })
      .finally(() => setIsLoading(false))
  }, [application, hasLoaded])

  const downloadEsisDocument = async (): Promise<void> => {
    if (!applicationData) return

    const body = {
      s3Key: applicationData.esisS3Key,
    }

    setIsDownloading(true)
    try {
      const response = await axios.post(`${MY_MOJO_API}/documents/download-url`, body)
      if (response.status !== 200) {
        setIsDownloading(false)
        throw new Error("Error downloading Mortgage Illustration")
      }
      const url = response.data.downloadUrl
      setIsDownloading(false)
      window.open(url)
    } catch (error) {
      console.error(error)
      setIsDownloading(false)
      alert("Error downloading Mortgage Illustration")
    }
  }

  return (
    <Root>
      {isError ? (
        <RenderError />
      ) : isLoading ? (
        <RenderLoading />
      ) : (
        <Recommendation
          applicationData={applicationData!}
          downloadEsisDocument={downloadEsisDocument}
          isDownloading={isDownloading}
          isPreview={true}
          acceptRecommendationOpportunity={() => undefined}
          acceptRecommendationQuote={() => undefined}
          previewData={applicationData!.previewData}
        />
      )}
    </Root>
  )
}

const getApplicationData = async (
  token: string,
  timestamp: string
): Promise<ApplicationDataProps | null> => {
  let response: AxiosResponse<ApplicationDataProps>
  if (timestamp) {
    response = await axios.get(
      `${process.env.MY_MOJO_API}/specific-recommendation/${token}/${timestamp}`
    )
  } else {
    response = await axios.get(`${process.env.MY_MOJO_API}/rec-preview/${token}`)
  }
  if (response.status !== 200) {
    throw new Error("Could not find preview token")
  }
  return response.data
}
